import { FoodSource, foodSourceInfoTable } from '@cf-shared/data-model/food';
import { LL } from 'i18n/i18n.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';

/**
 * The text shown in the Edit Food "where did this food come from?" dialog:
 * the dialog title, plus the attribution paragraph above the trust-level note.
 */
export interface FoodSourceDisplay {
  title: TranslatedString;
  attribution: TranslatedString;
}

/**
 * The Edit Food screen's own row for one source: the dialog's title and
 * attribution, and the short name on the badge that opens it.
 *
 * Every field is a function rather than a string because the translated ones
 * must be read at the moment they are shown — the person can change language
 * without reloading the app, so a string captured when this file loaded would
 * be the wrong language from then on.
 *
 * `shortName` is optional. Left out, the badge shows the short name the shared
 * source table already carries, so the acronyms have one home.
 */
interface FoodSourceCopy {
  title: () => TranslatedString;
  attribution: () => TranslatedString;
  shortName?: () => TranslatedString;
}

// the same USDA citation covers both USDA datasets we ingest
const USDA_CITATION =
  'U.S. Department of Agriculture, Agricultural Research Service, Beltsville Human Nutrition Research Center. FoodData Central. <https://fdc.nal.usda.gov/>';

/**
 * Which of our own copy each source shows, and which published text it shows.
 *
 * Three of these sources are the app's own writing, so they belong in the
 * translation catalog like any other UI copy — those are the rows with a
 * `shortName`, because their badge text is our wording too. The rest name a
 * third-party organization and quote its citation or license attribution —
 * including Open Food Facts' ODbL "© Open Food Facts contributors" line — so
 * they are reproduced exactly as published. Translating an official
 * organization name or a license notice would misidentify the source, which is
 * why they stay English in every language, the same way the app's legal copy
 * does. See `design-docs/localization-scope.md`.
 *
 * A source with no row here shows nothing: the caller hides the source row
 * entirely for a food whose id names no source we know.
 */
const foodSourceCopyTable: { [source in FoodSource]?: FoodSourceCopy } = {
  [FoodSource.OFR]: {
    title: () => LL.editFood.foodSources.appRecipe.name(),
    attribution: () => LL.editFood.foodSources.appRecipe.description(),
    shortName: () => LL.editFood.foodSources.appRecipe.shortName(),
  },
  [FoodSource.UDF]: {
    title: () => LL.editFood.foodSources.community.name(),
    attribution: () => LL.editFood.foodSources.community.description(),
    shortName: () => LL.editFood.foodSources.community.shortName(),
  },
  [FoodSource.STP]: {
    title: () => LL.editFood.foodSources.staple.name(),
    attribution: () => LL.editFood.foodSources.staple.description(),
    shortName: () => LL.editFood.foodSources.staple.shortName(),
  },
  [FoodSource.USDA]: {
    title: () => untranslatedString('USDA Standard Reference Food'),
    attribution: () => untranslatedString(USDA_CITATION),
  },
  [FoodSource.FDC]: {
    title: () => untranslatedString('USDA FoodData Central Branded Food'),
    attribution: () => untranslatedString(USDA_CITATION),
  },
  [FoodSource.NFSA]: {
    title: () => untranslatedString('Norwegian Food Safety Authority'),
    attribution: () => untranslatedString('Matvaretabellen, Norwegian Food Safety Authority. <https://www.matvaretabellen.no/en/>'),
  },
  [FoodSource.OFF]: {
    title: () => untranslatedString('Open Food Facts'),
    // the stray ". ," is how this line has always read; it is copied
    // across unchanged rather than tidied, so this move changes no text
    attribution: () => untranslatedString('Open Food Facts. , © Open Food Facts contributors, <https://world.openfoodfacts.org/>'),
  },
  [FoodSource.ONF]: {
    title: () => untranslatedString('OpenNutrition Food'),
    attribution: () => untranslatedString(
      'OpenNutrition Food. <https://www.opennutrition.app>\n\n' +
      'Portions of the OpenNutrition dataset incorporate data from [Open Food Facts](https://world.openfoodfacts.org), © Open Food Facts contributors'),
  },
};

/** the dialog title and attribution paragraph for one source */
export function getFoodSourceDisplay(source: FoodSource): FoodSourceDisplay {
  const copy = foodSourceCopyTable[source];
  // an unrecognised food id has no source to name. the caller hides the
  // source row entirely in that case, so this is never displayed.
  if (copy === undefined) {
    return { title: untranslatedString(''), attribution: untranslatedString('') };
  }
  return { title: copy.title(), attribution: copy.attribution() };
}

/**
 * The short source name shown on the Edit Food source badge (the small link the
 * user taps to open the dialog above).
 *
 * A source whose row gives no short name of its own reads the one the shared
 * source table carries — that covers the acronyms and third-party names that
 * read the same in every language (USDA, FDC, OFF, NFSA, ONF), so there is a
 * single source of truth for those.
 */
export function getFoodSourceShortName(source: FoodSource): TranslatedString {
  const ownShortName = foodSourceCopyTable[source]?.shortName;
  if (ownShortName !== undefined) {
    return ownShortName();
  }
  return untranslatedString(foodSourceInfoTable[source]?.shortName ?? '');
}
